import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Edit, Trash2, Plus, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminClasses = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingClass, setEditingClass] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { toast: shadToast } = useToast();

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAvailability = async (classId) => {
    try {
      await axios.patch(`${API}/classes/${classId}/availability`);
      toast.success("Class availability updated.");
      fetchClasses();
    } catch (error) {
      toast.error("Failed to update availability.");
    }
  };

  const toggleComingSoon = async (classId) => {
    try {
      await axios.patch(`${API}/classes/${classId}/coming-soon`);
      toast.success("Coming Soon status updated.");
      fetchClasses();
    } catch (error) {
      toast.error("Failed to update status.");
    }
  };

  const toggleMaintenance = async (classId) => {
    try {
      await axios.patch(`${API}/classes/${classId}/maintenance`);
      toast.success("Maintenance mode updated.");
      fetchClasses();
    } catch (error) {
      toast.error("Failed to update maintenance mode.");
    }
  };

  const handleCreate = () => {
    setEditingClass({
      name: '',
      description: '',
      duration: '',
      price: 0,
      maxSeats: null,
      available: true,
      comingSoon: false,
      highlights: '',
      booking_url: ''
    });
    setIsCreating(true);
    setIsDialogOpen(true);
  };

  const handleEdit = (classItem) => {
    setEditingClass({
      ...classItem,
      highlights: classItem.highlights.join('\n')
    });
    setIsCreating(false);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const updateData = {
        ...editingClass,
        highlights: editingClass.highlights.split('\n').filter(h => h.trim())
      };
      
      if (isCreating) {
        await axios.post(`${API}/classes`, updateData);
        toast.success("Class created successfully.");
      } else {
        await axios.put(`${API}/classes/${editingClass.id}`, updateData);
        toast.success("Class updated successfully.");
      }
      setIsDialogOpen(false);
      fetchClasses();
    } catch (error) {
      toast.error("Failed to save class.");
    }
  };

  const handleDelete = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/classes/${classId}`);
      toast.success("Class deleted successfully.");
      fetchClasses();
    } catch (error) {
      toast.error("Failed to delete class.");
    }
  };

  const getStatusBadge = (classItem) => {
    if (classItem.maintenanceMode) {
      return <Badge className="bg-orange-600">Check Back Soon</Badge>;
    }
    if (classItem.comingSoon) {
      return <Badge className="bg-blue-600">Coming Soon</Badge>;
    }
    if (classItem.available) {
      return <Badge className="bg-green-600">Available</Badge>;
    }
    return <Badge className="bg-gray-600">Unavailable</Badge>;
  };

  const getRemainingSeats = (classItem) => {
    if (!classItem.maxSeats) return 'Unlimited';
    return classItem.maxSeats - (classItem.bookedSeats || 0);
  };

  if (loading) {
    return (
      <AdminLayout title="Manage Classes">
        <div className="text-center py-8">Loading classes...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Manage Classes">
      <div className="mb-6">
        <Button onClick={handleCreate} className="bg-red-600 hover:bg-red-700">
          <Plus size={16} className="mr-2" />
          Add New Class
        </Button>
      </div>

      <div className="space-y-6">
        {classes.map((classItem) => (
          <Card key={classItem.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <CardTitle className="text-xl">{classItem.name}</CardTitle>
                    {getStatusBadge(classItem)}
                  </div>
                  <p className="text-gray-600">{classItem.description}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(classItem)}
                  >
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(classItem.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600">Duration</p>
                  <p className="font-semibold">{classItem.duration}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Price</p>
                  <p className="font-semibold text-red-600">${classItem.price}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Max Seats</p>
                  <p className="font-semibold">
                    {classItem.maxSeats || 'Unlimited'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Seats Remaining</p>
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-blue-600" />
                    <p className="font-semibold">{getRemainingSeats(classItem)}</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`available-${classItem.id}`} className="text-sm">
                    Available for Booking
                  </Label>
                  <Switch
                    id={`available-${classItem.id}`}
                    checked={classItem.available}
                    onCheckedChange={() => toggleAvailability(classItem.id)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor={`coming-${classItem.id}`} className="text-sm">
                    Coming Soon Badge
                  </Label>
                  <Switch
                    id={`coming-${classItem.id}`}
                    checked={classItem.comingSoon || false}
                    onCheckedChange={() => toggleComingSoon(classItem.id)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor={`maintenance-${classItem.id}`} className="text-sm">
                    Check Back Soon
                  </Label>
                  <Switch
                    id={`maintenance-${classItem.id}`}
                    checked={classItem.maintenanceMode || false}
                    onCheckedChange={() => toggleMaintenance(classItem.id)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isCreating ? 'Create New Class' : 'Edit Class'}</DialogTitle>
            <DialogDescription>
              {isCreating ? 'Add a new class to your offerings.' : 'Update class information, pricing, and details.'}
            </DialogDescription>
          </DialogHeader>
          {editingClass && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Class Name *</Label>
                <Input
                  id="name"
                  value={editingClass.name}
                  onChange={(e) => setEditingClass({ ...editingClass, name: e.target.value })}
                  placeholder="BLS (Basic Life Support)"
                />
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={editingClass.description}
                  onChange={(e) => setEditingClass({ ...editingClass, description: e.target.value })}
                  rows={3}
                  placeholder="American Heart Association BLS certification..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Duration *</Label>
                  <Input
                    id="duration"
                    value={editingClass.duration}
                    onChange={(e) => setEditingClass({ ...editingClass, duration: e.target.value })}
                    placeholder="4-5 hours"
                  />
                </div>
                <div>
                  <Label htmlFor="price">Price ($) *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={editingClass.price}
                    onChange={(e) => setEditingClass({ ...editingClass, price: parseFloat(e.target.value) || 0 })}
                    placeholder="65"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="maxSeats">Maximum Seats (leave empty for unlimited)</Label>
                <Input
                  id="maxSeats"
                  type="number"
                  value={editingClass.maxSeats || ''}
                  onChange={(e) => setEditingClass({ ...editingClass, maxSeats: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="20"
                />
                <p className="text-xs text-gray-500 mt-1">Set a limit on how many students can enroll</p>
              </div>
              <div>
                <Label htmlFor="booking_url">Zoho Booking URL</Label>
                <Input
                  id="booking_url"
                  value={editingClass.booking_url || ''}
                  onChange={(e) => setEditingClass({ ...editingClass, booking_url: e.target.value })}
                  placeholder="https://zbooking.us/your-class-link"
                />
                <p className="text-xs text-gray-500 mt-1">Paste the specific Zoho booking link for this class. Leave empty to use the general booking page.</p>
              </div>
              <div>
                <Label htmlFor="highlights">Highlights (one per line) *</Label>
                <Textarea
                  id="highlights"
                  value={editingClass.highlights}
                  onChange={(e) => setEditingClass({ ...editingClass, highlights: e.target.value })}
                  rows={5}
                  placeholder="AHA BLS Provider eCard\nHigh-quality CPR skills\nAED usage training"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} className="bg-red-600 hover:bg-red-700">
                  {isCreating ? 'Create Class' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
